import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  businessAppPeopleQuery,
  saveBusinessApp,
  type AppState,
  type BusinessAppDetail,
  type Choice,
  type NewPerson,
} from '../api/businessApps'
import { ApiError } from '../api/client'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { draftProblem, OwnerPicker, saveOwnerDraft } from './OwnerPicker'

/**
 * Bucatile comune ale ecranelor Applications: pastilele (criticitate, stare,
 * sanatate) si formularul aplicatiei, folosit si la adaugare (lista) si la
 * modificare (fisa).
 *
 * Tot textul scris de om (numele, furnizorul, descrierea) ajunge in pagina ca
 * text React - scapat - niciodata ca HTML.
 */

export const STYLES = ['css/adm.css']

export const appUrl = (id: number) => `/app/applications/${id}`

/** Harta aplicatiilor clientului (`map/ApplicationMapPage`); `appId` deschide pagina la cardul ei. */
export const mapUrl = (companyId: number, appId?: number) =>
  `/app/applications/map${companyId > 0 ? `?company=${companyId}` : ''}${appId ? `#app-${appId}` : ''}`

const STATE_PILL: Record<AppState, string> = {
  down: 'adm-pill-bad',
  alarm: 'adm-pill-bad',
  degraded: 'adm-pill-warn',
  ok: 'adm-pill-ok',
  empty: '',
}

export function StatePill({ state, label }: { state: AppState; label: string }) {
  return <span className={`adm-pill ${STATE_PILL[state] ?? ''}`.trim()}>{label}</span>
}

/** 4 Critical, 3 High: rosu / galben; restul neutru. */
export function CriticalityPill({ value, label }: { value: number; label: string }) {
  const pill = value >= 4 ? 'adm-pill-bad' : value === 3 ? 'adm-pill-warn' : ''
  return <span className={`adm-pill ${pill}`.trim()}>{label}</span>
}

/** it_ci.health: 3 down, 2 degraded, 1 operational; null = fara CI. */
export function HealthPill({ health, label }: { health: number | null; label: string }) {
  if (health === null || label === '') return <span className="adm-muted">-</span>
  const pill = health >= 3 ? 'adm-pill-bad' : health === 2 ? 'adm-pill-warn' : 'adm-pill-ok'
  return <span className={`adm-pill ${pill}`}>{label}</span>
}

export function failed(error: unknown): string {
  return error instanceof ApiError || error instanceof Error ? error.message : 'That could not be saved.'
}

type FormApp = BusinessAppDetail['app'] | null

/**
 * Adauga (`app` null) sau modifica aplicatia. O aplicatie noua e a firmei din
 * bara (`companyId`) - aplicatiile nu se mai vad, deci nici nu se adauga, pe
 * alta firma decat cea aleasa sus, nici cu "All companies".
 *
 * Proprietarul se alege cu `OwnerPicker` (cautare + "Add a person"): o
 * persoana noua se scrie intai la client, apoi pleaca `owner_contact_id`.
 */
export function AppFormModal({
  app,
  companyId,
  companyName,
  criticality,
  onClose,
  onSaved,
}: {
  app: FormApp
  companyId: number
  /** Numele firmei din bara, pentru "Add a person" la o aplicatie noua. */
  companyName?: string
  criticality: Choice[]
  onClose(): void
  onSaved(id: number): void
}) {
  const query = useQueryClient()
  const [name, setName] = useState(app?.name ?? '')
  const company = app?.companyId ?? companyId
  const [vendor, setVendor] = useState(app?.vendor ?? '')
  const [version, setVersion] = useState(app?.version ?? '')
  const [crit, setCrit] = useState<number>(app?.criticality ?? 2)
  const [owner, setOwner] = useState<number>(app?.ownerContactId ?? 0)
  const [ownerDraft, setOwnerDraft] = useState<NewPerson | null>(null)
  const [description, setDescription] = useState(app?.description ?? '')
  const [notes, setNotes] = useState(app?.notes ?? '')
  const [busy, setBusy] = useState(false)
  const people = useQuery(businessAppPeopleQuery(company))

  const submit = () => {
    if (name.trim() === '') {
      toast('The application needs a name.', 'Applications', 'error')
      return
    }
    const touched = ownerDraft !== null && Object.values(ownerDraft).some((v) => v.trim() !== '')
    const problem = touched ? draftProblem(ownerDraft) : null
    if (problem !== null) {
      toast(problem, 'Applications', 'error')
      return
    }
    setBusy(true)
    const ownerId = async (): Promise<number> => {
      if (!touched || ownerDraft === null) return owner
      const person = await saveOwnerDraft(company, ownerDraft)
      /* Scrisa o data: daca salvarea aplicatiei cade, a doua apasare n-o mai scrie. */
      setOwner(person.id)
      setOwnerDraft(null)
      void query.invalidateQueries({ queryKey: ['business-apps', 'people', company] })
      return person.id
    }
    ownerId()
      .then((ownerContactId) =>
        saveBusinessApp({
          ID: app?.id ?? 0,
          company_id: company,
          name,
          vendor,
          version,
          criticality: crit,
          owner_contact_id: ownerContactId,
          description,
          notes,
        }),
      )
      .then((result) => {
        if (result.status !== 'OK') {
          toast(result.message, 'Applications', 'error')
          return
        }
        toast(result.message, 'Applications')
        void query.invalidateQueries({ queryKey: ['business-apps'] })
        onSaved(result.id ?? app?.id ?? 0)
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id="appFormModal"
      open
      onClose={onClose}
      title={app === null ? 'Add application' : `Edit ${app.name}`}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" id="appFormSave" disabled={busy} onClick={submit}>
            Save
          </button>
        </>
      }
    >
      <div className="form-group">
        <label htmlFor="appName">Name</label>
        <input id="appName" className="form-control" maxLength={190} value={name} placeholder="e.g. Charisma ERP" onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="row">
        <div className="col-sm-6 form-group">
          <label htmlFor="appVendor">Vendor / manufacturer</label>
          <input id="appVendor" className="form-control" maxLength={190} value={vendor} onChange={(e) => setVendor(e.target.value)} />
        </div>
        <div className="col-sm-3 form-group">
          <label htmlFor="appVersion">Version</label>
          <input id="appVersion" className="form-control" maxLength={100} value={version} onChange={(e) => setVersion(e.target.value)} />
        </div>
        <div className="col-sm-3 form-group">
          <label htmlFor="appCriticality">Criticality</label>
          <select id="appCriticality" className="form-control" value={crit} onChange={(e) => setCrit(Number(e.target.value))}>
            {criticality.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="appOwner">Owner at the customer</label>
        <p className="app-owner-note">Pick them from the customer&apos;s people or add them if they are not there.</p>
        <OwnerPicker
          selectId="appOwner"
          people={people.data?.people ?? []}
          value={owner}
          onChange={setOwner}
          draft={ownerDraft}
          onDraft={setOwnerDraft}
          emptyLabel="- nobody named -"
          customerName={app?.companyName ?? companyName}
          disabled={busy || company < 1}
        />
      </div>
      <div className="form-group">
        <label htmlFor="appDescription">Description</label>
        <textarea id="appDescription" className="form-control" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <div className="form-group">
        <label htmlFor="appNotes">Notes</label>
        <textarea id="appNotes" className="form-control" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
    </Modal>
  )
}
